import React, { useEffect, useMemo, useState } from 'react'
import { usePersistentTab } from '../../hooks/usePersistentTab'
import { AlertCircle, Edit2, Plus, Trash2, X, GripVertical } from 'lucide-react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import ConfirmModal from '../../components/ConfirmModal.jsx'
import Notification from '../../components/Notification.jsx'
import { supabase } from '../../lib/supabaseClient'

const DOMAINS = [
  {
    key: 'curso',
    label: 'Categorias Cursos',
    title: 'Categorias de Cursos',
    description: 'Gerencie as categorias disponíveis para os cursos.',
  },
  {
    key: 'biblioteca',
    label: 'Categorias Biblioteca',
    title: 'Categorias da Biblioteca',
    description: 'Gerencie as categorias disponíveis para a biblioteca.',
  },
  {
    key: 'vitrini',
    label: 'Vitrini',
    title: 'Categorias da Vitrini',
    description: 'Gerencie as categorias que irão compor a tela de Vitrini.',
  },
]

const defaultDomainState = () => ({ items: [], loading: true, error: '' })

function normalizeErrorMessage(err, fallback) {
  const msg = String(err?.message || err || fallback || 'Erro inesperado.')
  return msg.replace('permission denied', 'Permissão insuficiente')
}

function validateCategoryForm({ name, status }) {
  const errors = {}
  const n = String(name || '').trim()
  const s = status

  if (!n) errors.name = 'Nome é obrigatório.'
  else if (n.length < 2) errors.name = 'Nome deve ter pelo menos 2 caracteres.'
  else if (n.length > 60) errors.name = 'Nome deve ter no máximo 60 caracteres.'

  if (typeof s !== 'boolean') errors.status = 'Status inválido.'

  return { ok: Object.keys(errors).length === 0, errors, normalized: { name: n, status: s } }
}

function Tabs({ active, onChange }) {
  return (
    <div className="border-b border-slate-800 mb-6 overflow-x-auto">
      <div className="flex gap-2 min-w-max">
        {DOMAINS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-wide transition border-b-2 ${
              active === t.key
                ? 'text-teal-400 border-teal-400'
                : 'text-slate-500 border-transparent hover:text-slate-300'
            }`}
            aria-current={active === t.key ? 'page' : undefined}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl">
      <div className="p-5 border-b border-slate-800 bg-slate-950/80">
        <div className="h-4 w-40 bg-slate-800 rounded animate-pulse" />
      </div>
      <div className="divide-y divide-slate-800/50">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="p-5 flex items-center gap-4">
            <div className="h-4 w-40 bg-slate-800 rounded animate-pulse" />
            <div className="h-4 flex-1 bg-slate-800 rounded animate-pulse hidden md:block" />
            <div className="h-6 w-20 bg-slate-800 rounded animate-pulse" />
            <div className="h-8 w-20 bg-slate-800 rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  )
}

function ErrorBanner({ message }) {
  return (
    <div className="bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-xl p-4 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5" size={18} />
        <div>
          <div className="font-bold">Falha ao carregar categorias</div>
          <div className="text-sm text-rose-200/90">{message}</div>
        </div>
      </div>
    </div>
  )
}

function EmptyState({ onCreate }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center">
      <div className="text-slate-300 font-bold mb-1">Nenhuma categoria cadastrada.</div>
      <div className="text-slate-500 text-sm mb-6">Crie a primeira categoria para começar.</div>
      <button
        type="button"
        onClick={onCreate}
        className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white px-5 py-2.5 rounded-lg font-bold inline-flex items-center gap-2"
      >
        <Plus size={18} /> Nova categoria
      </button>
    </div>
  )
}

function CategoriesTable({ items, onEdit, onDelete, busyId, activeDomain, courseCategories }) {
  const isVitrini = activeDomain === 'vitrini'

  if (!isVitrini) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto hidden md:block">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold">
            <tr>
              {isVitrini && <th className="p-5 w-10"></th>}
              <th className="p-5">Nome</th>
              {isVitrini ? <th className="p-5">Categorias Vinculadas</th> : <th className="p-5">Descrição</th>}
              <th className="p-5">Status</th>
              <th className="p-5 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {items.map((c) => (
              <tr
                key={c.id}
                className="transition text-slate-300 hover:bg-slate-800/50"
              >
                {isVitrini && (
                  <td className="p-5 w-10">
                    <div className="text-slate-600 p-1">
                      <GripVertical size={20} />
                    </div>
                  </td>
                )}
                <td className="p-5 font-bold text-white">{c.name}</td>
                <td className="p-5 text-sm text-slate-400">
                  {isVitrini ? (
                    <div className="flex flex-wrap gap-1">
                      {(c.linked_category_ids || []).length > 0 ? (
                        (c.linked_category_ids || []).map(id => {
                          const name = courseCategories.find(cc => cc.id === id)?.name || '...'
                          return (
                            <span key={id} className="bg-slate-800 px-2 py-0.5 rounded-full text-[10px] border border-slate-700 text-slate-300">
                              {name}
                            </span>
                          )
                        })
                      ) : (
                        <span className="italic text-slate-500">Nenhum vínculo</span>
                      )}
                    </div>
                  ) : (
                    c.description || ''
                  )}
                </td>
                <td className="p-5">
                  <span
                    className={`px-2 py-1 rounded text-[10px] font-bold border uppercase ${
                      c.status
                        ? 'border-teal-500/20 bg-teal-500/10 text-teal-400'
                        : 'border-rose-500/20 bg-rose-500/10 text-rose-400'
                    }`}
                  >
                    {c.status ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="p-5">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(c)}
                      className="px-3 py-2 rounded-lg border border-slate-800 text-slate-300 hover:text-white hover:border-slate-700 transition inline-flex items-center gap-2"
                      aria-label={`Editar categoria ${c.name}`}
                      disabled={busyId === c.id}
                    >
                      <Edit2 size={16} /> Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(c)}
                      className="px-3 py-2 rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition inline-flex items-center gap-2"
                      aria-label={`Excluir categoria ${c.name}`}
                      disabled={busyId === c.id}
                    >
                      <Trash2 size={16} /> Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto hidden md:block">
      <table className="w-full text-left border-collapse">
        <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold">
          <tr>
            {isVitrini && <th className="p-5 w-10"></th>}
            <th className="p-5">Nome</th>
            {isVitrini ? <th className="p-5">Categorias Vinculadas</th> : <th className="p-5">Descrição</th>}
            <th className="p-5">Status</th>
            <th className="p-5 text-right">Ações</th>
          </tr>
        </thead>
        <Droppable droppableId={`categories-table-${activeDomain}`} isDropDisabled={!isVitrini}>
          {(provided) => (
            <tbody
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="divide-y divide-slate-800/50"
            >
              {items.map((c, index) => (
                <Draggable key={String(c.id)} draggableId={String(c.id)} index={index} isDragDisabled={!isVitrini}>
                  {(provided, snapshot) => (
                    <tr
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`transition text-slate-300 ${
                        snapshot.isDragging ? 'bg-slate-800 shadow-2xl z-50' : 'hover:bg-slate-800/50'
                      }`}
                    >
                      {isVitrini && (
                        <td className="p-5 w-10">
                          <div {...provided.dragHandleProps} className="text-slate-600 hover:text-slate-400 cursor-grab active:cursor-grabbing p-1">
                            <GripVertical size={20} />
                          </div>
                        </td>
                      )}
                      <td className="p-5 font-bold text-white">{c.name}</td>
                      <td className="p-5 text-sm text-slate-400">
                        {isVitrini ? (
                          <div className="flex flex-wrap gap-1">
                            {(c.linked_category_ids || []).length > 0 ? (
                              (c.linked_category_ids || []).map(id => {
                                const name = courseCategories.find(cc => cc.id === id)?.name || '...'
                                return (
                                  <span key={id} className="bg-slate-800 px-2 py-0.5 rounded-full text-[10px] border border-slate-700 text-slate-300">
                                    {name}
                                  </span>
                                )
                              })
                            ) : (
                              <span className="italic text-slate-500">Nenhum vínculo</span>
                            )}
                          </div>
                        ) : (
                          c.description || ''
                        )}
                      </td>
                      <td className="p-5">
                        <span
                          className={`px-2 py-1 rounded text-[10px] font-bold border uppercase ${
                            c.status
                              ? 'border-teal-500/20 bg-teal-500/10 text-teal-400'
                              : 'border-rose-500/20 bg-rose-500/10 text-rose-400'
                          }`}
                        >
                          {c.status ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="p-5">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onEdit(c)}
                            className="px-3 py-2 rounded-lg border border-slate-800 text-slate-300 hover:text-white hover:border-slate-700 transition inline-flex items-center gap-2"
                            aria-label={`Editar categoria ${c.name}`}
                            disabled={busyId === c.id}
                          >
                            <Edit2 size={16} /> Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(c)}
                            className="px-3 py-2 rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition inline-flex items-center gap-2"
                            aria-label={`Excluir categoria ${c.name}`}
                            disabled={busyId === c.id}
                          >
                            <Trash2 size={16} /> Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </tbody>
          )}
        </Droppable>
      </table>
    </div>
  )
}

function CategoriesCards({ items, onEdit, onDelete, busyId, activeDomain, courseCategories }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:hidden">
      {items.map((c) => (
        <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-white font-bold truncate">{c.name}</div>
              {activeDomain === 'vitrini' ? (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(c.linked_category_ids || []).length > 0 ? (
                    (c.linked_category_ids || []).map(id => {
                      const name = courseCategories.find(cc => cc.id === id)?.name || '...'
                      return (
                        <span key={id} className="bg-slate-800 px-2 py-0.5 rounded-full text-[10px] border border-slate-700 text-slate-300">
                          {name}
                        </span>
                      )
                    })
                  ) : (
                    <span className="italic text-slate-500 text-[10px]">Nenhum vínculo</span>
                  )}
                </div>
              ) : (
                c.description ? <div className="text-slate-400 text-sm mt-1">{c.description}</div> : null
              )}
            </div>
            <span
              className={`px-2 py-1 rounded text-[10px] font-bold border uppercase shrink-0 ${
                c.status
                  ? 'border-teal-500/20 bg-teal-500/10 text-teal-400'
                  : 'border-rose-500/20 bg-rose-500/10 text-rose-400'
              }`}
            >
              {c.status ? 'Ativo' : 'Inativo'}
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => onEdit(c)}
              className="px-4 py-2 rounded-lg border border-slate-800 text-slate-300 hover:text-white hover:border-slate-700 transition inline-flex items-center justify-center gap-2"
              disabled={busyId === c.id}
            >
              <Edit2 size={16} /> Editar
            </button>
            <button
              type="button"
              onClick={() => onDelete(c)}
              className="px-4 py-2 rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition inline-flex items-center justify-center gap-2"
              disabled={busyId === c.id}
            >
              <Trash2 size={16} /> Excluir
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

function CategoryModal({ open, partner, mode, value, errors, saving, onChange, onClose, onSubmit, activeDomain, courseCategories }) {
  if (!open) return null

  const nameId = 'category-name'
  const statusId = 'category-status'

  const toggleLinkedCategory = (id) => {
    const current = Array.isArray(value.linked_category_ids) ? value.linked_category_ids : []
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id]
    onChange({ ...value, linked_category_ids: next })
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl p-6 my-auto">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h3 className="text-xl font-bold text-white">{mode === 'edit' ? 'Editar Categoria' : 'Nova Categoria'}</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-white transition" aria-label="Fechar modal">
            <X size={22} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={nameId} className="text-xs font-bold text-slate-500 uppercase tracking-widest">Nome</label>
            <input
              id={nameId}
              type="text"
              value={value.name}
              onChange={(e) => onChange({ ...value, name: e.target.value })}
              className={`bg-slate-950 border p-3 rounded-lg text-white focus:border-white transition outline-none ${
                errors.name ? 'border-rose-500/60' : 'border-slate-800'
              }`}
              aria-invalid={errors.name ? 'true' : 'false'}
            />
            {errors.name ? <div className="text-rose-300 text-sm">{errors.name}</div> : null}
          </div>

          {activeDomain === 'vitrini' && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Vincular Categorias de Cursos</label>
              <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 max-h-48 overflow-y-auto flex flex-col gap-2">
                {courseCategories.length === 0 ? (
                  <div className="text-slate-500 text-sm italic">Nenhuma categoria de curso disponível.</div>
                ) : (
                  courseCategories.map((c) => (
                    <label key={c.id} className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={(value.linked_category_ids || []).includes(c.id)}
                        onChange={() => toggleLinkedCategory(c.id)}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-teal-500 focus:ring-teal-500/20 focus:ring-offset-0"
                      />
                      <span className="text-slate-300 text-sm group-hover:text-white transition">{c.name}</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-[10px] text-slate-500 italic">Selecione as categorias que farão parte desta vitrine.</p>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor={statusId} className="text-xs font-bold text-slate-500 uppercase tracking-widest">Status</label>
            <select
              id={statusId}
              value={value.status ? 'Ativo' : 'Inativo'}
              onChange={(e) => onChange({ ...value, status: e.target.value === 'Ativo' })}
              className={`bg-slate-950 border p-3 rounded-lg text-white outline-none ${
                errors.status ? 'border-rose-500/60' : 'border-slate-800'
              }`}
              aria-invalid={errors.status ? 'true' : 'false'}
            >
              <option value="Ativo">Ativo</option>
              <option value="Inativo">Inativo</option>
            </select>
            {errors.status ? <div className="text-rose-300 text-sm">{errors.status}</div> : null}
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ backgroundColor: partner?.color || '#f97316' }}
              className="px-8 py-2 rounded-lg text-white font-bold shadow-lg disabled:opacity-60"
            >
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function AdminCategories({ partner, categories, setCategories }) {
  const [activeDomain, setActiveDomain] = usePersistentTab('admin_categories', 'curso')
  const [domainState, setDomainState] = useState(() => ({
    curso: defaultDomainState(),
    biblioteca: defaultDomainState(),
    vitrini: defaultDomainState(),
  }))

  const [notification, setNotification] = useState(null)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null })

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const [editingId, setEditingId] = useState(null)
  const [modalValue, setModalValue] = useState({ name: '', status: true, linked_category_ids: [] })
  const [modalErrors, setModalErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState(null)

  const tabMeta = useMemo(() => DOMAINS.find((d) => d.key === activeDomain) || DOMAINS[0], [activeDomain])
  const activeItems = domainState[activeDomain]?.items || []
  const activeLoading = !!domainState[activeDomain]?.loading
  const activeError = String(domainState[activeDomain]?.error || '')
  const courseCategories = domainState.curso?.items || []

  const closeModal = () => {
    setModalOpen(false)
    setModalMode('create')
    setEditingId(null)
    setModalValue({ name: '', status: true, linked_category_ids: [] })
    setModalErrors({})
  }

  const openCreate = () => {
    setModalMode('create')
    setEditingId(null)
    setModalValue({ name: '', status: true, linked_category_ids: [] })
    setModalErrors({})
    setModalOpen(true)
  }

  const openEdit = (cat) => {
    setModalMode('edit')
    setEditingId(cat.id)
    setModalValue({
      name: cat.name || '',
      status: !!cat.status,
      linked_category_ids: Array.isArray(cat.linked_category_ids) ? cat.linked_category_ids : [],
    })
    setModalErrors({})
    setModalOpen(true)
  }

  const upsertGlobalCategories = (nextByDomain) => {
    const merged = [
      ...(nextByDomain.curso?.items || []),
      ...(nextByDomain.biblioteca?.items || []),
      ...(nextByDomain.vitrini?.items || []),
    ]
    if (typeof setCategories === 'function') setCategories(merged)
  }

  const loadDomain = async (domain) => {
    setDomainState((prev) => ({ ...prev, [domain]: { ...prev[domain], loading: true, error: '' } }))

    try {
      let q = supabase
        .from('course_categories')
        .select('id, name, description, status, domain, linked_category_ids, ordem')

      if (domain === 'vitrini') {
        q = q.order('ordem', { ascending: true })
      } else {
        q = q.order('name', { ascending: true })
      }

      q = q.eq('domain', domain)
      const { data, error } = await q
      if (error) throw error
      const items = (data || []).map((r) => ({
        id: r.id,
        name: r.name,
        description: r.description || '',
        status: typeof r.status === 'boolean' ? r.status : String(r.status || 'Ativo').toLowerCase() === 'ativo',
        domain: r.domain || domain,
        linked_category_ids: Array.isArray(r.linked_category_ids) ? r.linked_category_ids : [],
        ordem: r.ordem || 0,
      }))

      setDomainState((prev) => {
        const next = { ...prev, [domain]: { items, loading: false, error: '' } }
        upsertGlobalCategories(next)
        return next
      })
    } catch (err) {
      const msg = normalizeErrorMessage(err, 'Falha ao carregar categorias.')
      const isMissingDomain = /column\s+.*domain.*does not exist|domain.*does not exist/i.test(msg)
      if (isMissingDomain) {
        try {
          const { data, error } = await supabase
            .from('course_categories')
            .select('id, name, description, status')
            .order('name', { ascending: true })
          if (error) throw error
          const all = (data || []).map((r) => ({
            id: r.id,
            name: r.name,
            description: r.description || '',
            status: typeof r.status === 'boolean' ? r.status : String(r.status || 'Ativo').toLowerCase() === 'ativo',
          }))
          setDomainState((prev) => {
            const next = {
              ...prev,
              curso: { items: all, loading: false, error: '' },
              biblioteca: { items: [], loading: false, error: '' },
            }
            upsertGlobalCategories(next)
            return next
          })
          return
        } catch (fallbackErr) {
          setDomainState((prev) => ({ ...prev, [domain]: { ...prev[domain], loading: false, error: normalizeErrorMessage(fallbackErr) } }))
          return
        }
      }

      setDomainState((prev) => ({ ...prev, [domain]: { ...prev[domain], loading: false, error: msg } }))
    }
  }

  useEffect(() => {
    loadDomain('curso')
    loadDomain('biblioteca')
    loadDomain('vitrini')
  }, [])

  useEffect(() => {
    const list = Array.isArray(categories) ? categories : []
    const hasDomain = list.some((c) => c && typeof c.domain === 'string')
    if (!list.length) return
    if (!hasDomain) return
    setDomainState((prev) => {
      const normalize = (c) => ({
        id: c.id,
        name: c.name,
        description: c.description || '',
        status: typeof c.status === 'boolean' ? c.status : String(c.status || 'Ativo').toLowerCase() === 'ativo',
        domain: c.domain,
        linked_category_ids: Array.isArray(c.linked_category_ids) ? c.linked_category_ids : [],
      })
      const by = {
        curso: list.filter((c) => c.domain === 'curso').map(normalize),
        biblioteca: list.filter((c) => c.domain === 'biblioteca').map(normalize),
        vitrini: list.filter((c) => c.domain === 'vitrini').map(normalize),
      }
      return {
        ...prev,
        curso: { ...prev.curso, items: by.curso },
        biblioteca: { ...prev.biblioteca, items: by.biblioteca },
        vitrini: { ...prev.vitrini, items: by.vitrini },
      }
    })
  }, [categories])

  const handleSubmit = async (e) => {
    e.preventDefault()
    const { ok, errors, normalized } = validateCategoryForm(modalValue)
    setModalErrors(errors)
    if (!ok) return

    setSaving(true)
    try {
      if (modalMode === 'edit' && editingId) {
        setBusyId(editingId)
        const updatePayload = { name: normalized.name, status: normalized.status }
        if (activeDomain === 'vitrini') {
          updatePayload.linked_category_ids = modalValue.linked_category_ids
        }
        const { error } = await supabase
          .from('course_categories')
          .update(updatePayload)
          .eq('id', editingId)
        if (error) throw error
        setNotification({ message: 'Categoria atualizada com sucesso!', type: 'success' })
      } else {
        const payload = {
          name: normalized.name,
          status: normalized.status,
          domain: activeDomain,
          ordem: activeItems.length, // Add at the end
        }
        if (activeDomain === 'vitrini') {
          payload.linked_category_ids = modalValue.linked_category_ids
        }
        const { error } = await supabase.from('course_categories').insert(payload)
        if (error) throw error
        setNotification({ message: 'Categoria criada com sucesso!', type: 'success' })
      }

      await loadDomain(activeDomain)
      closeModal()
    } catch (err) {
      setNotification({ message: normalizeErrorMessage(err, 'Falha ao salvar categoria.'), type: 'error' })
    } finally {
      setSaving(false)
      setBusyId(null)
    }
  }

  const askDelete = (cat) => {
    setConfirmModal({
      isOpen: true,
      title: 'Excluir Categoria',
      message: `Tem certeza que deseja excluir a categoria "${cat.name}"?`,
      onConfirm: async () => {
        try {
          setBusyId(cat.id)
          const { error } = await supabase.from('course_categories').delete().eq('id', cat.id)
          if (error) throw error
          setNotification({ message: 'Categoria excluída com sucesso!', type: 'success' })
          await loadDomain(activeDomain)
        } catch (err) {
          setNotification({ message: normalizeErrorMessage(err, 'Falha ao excluir categoria.'), type: 'error' })
        } finally {
          setBusyId(null)
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        }
      },
    })
  }

  const handleDragEnd = async (result) => {
    if (activeDomain !== 'vitrini') return
    if (!result.destination) return
    if (result.destination.index === result.source.index) return

    const items = Array.from(activeItems)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    // Update local state immediately for snappy UI
    setDomainState((prev) => ({
      ...prev,
      [activeDomain]: { ...prev[activeDomain], items },
    }))

    try {
      // Prepare updates for Supabase
      const updates = items.map((item, index) => ({
        id: item.id,
        ordem: index,
      }))

      // We need to update multiple rows. Supabase update doesn't support multiple different values in one call easily with .update()
      // But we can use a RPC or just loop since it's usually a small number of categories.
      // Given the constraints, let's use a loop or a Promise.all
      const { error } = await supabase.rpc('reorder_categories', {
        p_updates: updates,
      })

      if (error) {
        // If RPC fails, try fallback to individual updates or throw
        console.error('RPC reorder_categories failed, falling back...', error)
        const promises = updates.map((u) =>
          supabase.from('course_categories').update({ ordem: u.ordem }).eq('id', u.id)
        )
        await Promise.all(promises)
      }
    } catch (err) {
      console.error('Error reordering categories:', err)
      setNotification({ message: 'Erro ao salvar nova ordem.', type: 'error' })
      // Revert local state on error
      loadDomain(activeDomain)
    }
  }

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      {notification ? <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} /> : null}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Categorias</h1>
          <p className="text-slate-400">Gerencie categorias de Cursos e da Biblioteca.</p>
        </div>
      </div>

      <Tabs
        active={activeDomain}
        onChange={(next) => {
          setActiveDomain(next)
          loadDomain(next)
        }}
      />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">{tabMeta.title}</h2>
          <p className="text-slate-500 text-sm">{tabMeta.description}</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          style={{ backgroundColor: partner?.color || '#f97316' }}
          className="text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition w-full md:w-auto justify-center"
        >
          <Plus size={20} /> Nova categoria
        </button>
      </div>

      {activeError ? <ErrorBanner message={activeError} /> : null}

      {activeLoading ? (
        <LoadingSkeleton />
      ) : activeItems.length === 0 ? (
        <EmptyState onCreate={openCreate} />
      ) : activeDomain === 'vitrini' ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <CategoriesTable items={activeItems} onEdit={openEdit} onDelete={askDelete} busyId={busyId} activeDomain={activeDomain} courseCategories={courseCategories} />
          <CategoriesCards items={activeItems} onEdit={openEdit} onDelete={askDelete} busyId={busyId} activeDomain={activeDomain} courseCategories={courseCategories} />
        </DragDropContext>
      ) : (
        <>
          <CategoriesTable items={activeItems} onEdit={openEdit} onDelete={askDelete} busyId={busyId} activeDomain={activeDomain} courseCategories={courseCategories} />
          <CategoriesCards items={activeItems} onEdit={openEdit} onDelete={askDelete} busyId={busyId} activeDomain={activeDomain} courseCategories={courseCategories} />
        </>
      )}

      <CategoryModal
        open={modalOpen}
        partner={partner}
        mode={modalMode}
        value={modalValue}
        errors={modalErrors}
        saving={saving}
        onChange={setModalValue}
        onClose={closeModal}
        onSubmit={handleSubmit}
        activeDomain={activeDomain}
        courseCategories={courseCategories}
      />
    </div>
  )
}
